import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { triggerAppointmentWhatsApp, triggerDoctorNewAppointmentWhatsApp, triggerDoctorCancellationWhatsApp } from "@/lib/auto-whatsapp-triggers";

export interface Appointment {
  id: string;
  clinic_id: string;
  patient_id: string;
  doctor_id: string | null;
  operatory_id: string | null;
  appointment_type_id: string | null;
  start_time: string;
  end_time: string;
  duration_minutes: number;
  status: "scheduled" | "confirmed" | "checked_in" | "in_progress" | "completed" | "cancelled" | "no_show";
  title: string | null;
  notes: string | null;
  reason_for_visit: string | null;
  patient_name: string;
  patient_phone: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  cancelled_at: string | null;
  cancelled_by: string | null;
  cancellation_reason: string | null;
  reminder_sent_at: string | null;
  confirmation_sent_at: string | null;
  // Follow-up fields
  parent_visit_id: string | null;
  is_follow_up: boolean;
  // Nested relations
  patient?: { id: string; name: string; phone: string; registration_complete: boolean };
  doctor?: { full_name: string; color?: string };
  operatory?: { name: string; code: string; color: string | null };
  appointment_type?: { name: string; color: string | null };
  clinic?: { name: string; code: string };
}

export interface AppointmentFilters {
  clinicId?: string;
  clinicIds?: string[]; // For multi-clinic "All Clinics" view
  operatoryId?: string;
  doctorId?: string;
  patientId?: string;
  status?: Appointment["status"];
  startDate?: string;
  endDate?: string;
}

export const useAppointments = (filters?: AppointmentFilters) => {
  return useQuery({
    queryKey: ["appointments", filters],
    queryFn: async () => {
      let query = supabase
        .from("appointments")
        .select(`
          *,
          patient:patients!inner(id, name, phone, registration_complete),
          doctor:profiles!appointments_doctor_id_fkey(full_name, color),
          operatory:operatories(name, code, color),
          appointment_type:appointment_types(name, color),
          clinic:clinics(name, code)
        `)
        .order("start_time", { ascending: true });

      if (filters?.clinicId) {
        query = query.eq("clinic_id", filters.clinicId);
      } else if (filters?.clinicIds?.length) {
        // Multi-clinic filter for "All Clinics" view
        query = query.in("clinic_id", filters.clinicIds);
      }
      if (filters?.operatoryId) {
        query = query.eq("operatory_id", filters.operatoryId);
      }
      // Filter by doctor - critical for doctor view mode
      if (filters?.doctorId) {
        query = query.eq("doctor_id", filters.doctorId);
      }
      if (filters?.patientId) {
        query = query.eq("patient_id", filters.patientId);
      }
      if (filters?.status) {
        query = query.eq("status", filters.status);
      }
      if (filters?.startDate) {
        query = query.gte("start_time", filters.startDate);
      }
      if (filters?.endDate) {
        query = query.lte("start_time", filters.endDate);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as any as Appointment[];
    },
    enabled: !!(filters?.clinicId || filters?.clinicIds?.length || filters?.doctorId),
  });
};

export const useAppointment = (id?: string) => {
  return useQuery({
    queryKey: ["appointment", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("appointments")
        .select(`
          *,
          patient:patients(id, name, phone, email, registration_complete, gender, date_of_birth, registration_number),
          doctor:profiles!appointments_doctor_id_fkey(full_name, color),
          operatory:operatories(name, code, color),
          appointment_type:appointment_types(name, color, default_duration_minutes),
          clinic:clinics(name, code)
        `)
        .eq("id", id!)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!id,
  });
};

export const useCheckAppointmentConflict = () => {
  return useMutation({
    mutationFn: async ({
      operatoryId,
      startTime,
      endTime,
      excludeId,
    }: {
      operatoryId: string;
      startTime: string;
      endTime: string;
      excludeId?: string;
    }) => {
      const { data, error } = await supabase.rpc("check_appointment_conflict", {
        p_operatory_id: operatoryId,
        p_start_time: startTime,
        p_end_time: endTime,
        p_exclude_appointment_id: excludeId || null,
      });

      if (error) throw error;
      return data;
    },
  });
};

export const useCheckDoctorConflict = () => {
  return useMutation({
    mutationFn: async ({
      doctorId,
      startTime,
      endTime,
      excludeId,
    }: {
      doctorId: string;
      startTime: string;
      endTime: string;
      excludeId?: string;
    }) => {
      const { data, error } = await supabase.rpc("check_doctor_appointment_conflict", {
        p_doctor_id: doctorId,
        p_start_time: startTime,
        p_end_time: endTime,
        p_exclude_appointment_id: excludeId || null,
      });

      if (error) throw error;
      return data;
    },
  });
};

export const useCreateAppointment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (appointment: Omit<Appointment, "id" | "created_at" | "updated_at" | "created_by">) => {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user?.id) {
        throw new Error("You must be logged in to create appointments");
      }
      
      const { data, error } = await supabase
        .from("appointments")
        .insert({
          ...appointment,
          created_by: user.id,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: async (data) => {
      queryClient.invalidateQueries({ queryKey: ["appointments"] });
      queryClient.invalidateQueries({ queryKey: ["enhanced-queue"] });
      queryClient.invalidateQueries({ queryKey: ["checked-in-appointments"] });
      queryClient.invalidateQueries({ queryKey: ["pending-communications"] });
      toast({
        title: "Success",
        description: "Appointment created successfully",
      });
      
      // Trigger SMS notification
      try {
        await supabase.functions.invoke('send-appointment-sms', {
          body: {
            appointmentId: data.id,
            notificationType: 'created',
          },
        });
      } catch (error) {
        console.error('Failed to send SMS notification:', error);
      }

      // Trigger WhatsApp Cloud API notification (fire-and-forget)
      triggerAppointmentWhatsApp(data.id, 'created');
      // Trigger doctor notification (fire-and-forget)
      triggerDoctorNewAppointmentWhatsApp(data.id);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });
};

export const useUpdateAppointment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, originalData, ...updates }: Partial<Appointment> & { id: string; originalData?: Appointment }) => {
      const { data, error } = await supabase
        .from("appointments")
        .update(updates)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return { data, originalData };
    },
    onSuccess: async ({ data, originalData }) => {
      queryClient.invalidateQueries({ queryKey: ["appointments"] });
      queryClient.invalidateQueries({ queryKey: ["appointment"] });
      queryClient.invalidateQueries({ queryKey: ["enhanced-queue"] });
      queryClient.invalidateQueries({ queryKey: ["checked-in-appointments"] });
      queryClient.invalidateQueries({ queryKey: ["pending-communications"] });
      toast({
        title: "Success",
        description: "Appointment updated successfully",
      });
      
      // Check if time was changed (rescheduled)
      if (originalData && 
          (originalData.start_time !== data.start_time || originalData.end_time !== data.end_time)) {
        try {
          await supabase.functions.invoke('send-appointment-sms', {
            body: {
              appointmentId: data.id,
              notificationType: 'rescheduled',
            },
          });
        } catch (error) {
          console.error('Failed to send SMS notification:', error);
        }

        // Trigger WhatsApp Cloud API notification (fire-and-forget)
        triggerAppointmentWhatsApp(data.id, 'rescheduled');
      }
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });
};

export const useCancelAppointment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      const { data: { user } } = await supabase.auth.getUser();

      const { data, error } = await supabase
        .from("appointments")
        .update({
          status: "cancelled",
          cancelled_at: new Date().toISOString(),
          cancelled_by: user?.id,
          cancellation_reason: reason,
        })
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: async (data) => {
      queryClient.invalidateQueries({ queryKey: ["appointments"] });
      queryClient.invalidateQueries({ queryKey: ["enhanced-queue"] });
      queryClient.invalidateQueries({ queryKey: ["checked-in-appointments"] });
      queryClient.invalidateQueries({ queryKey: ["pending-communications"] });
      toast({
        title: "Success",
        description: "Appointment cancelled successfully",
      });
      
      // Trigger SMS notification
      try {
        await supabase.functions.invoke('send-appointment-sms', {
          body: {
            appointmentId: data.id,
            notificationType: 'cancelled',
          },
        });
      } catch (error) {
        console.error('Failed to send SMS notification:', error);
      }

      // Trigger WhatsApp Cloud API notification (fire-and-forget)
      triggerAppointmentWhatsApp(data.id, 'cancelled');
      // Trigger doctor cancellation notification (fire-and-forget)
      triggerDoctorCancellationWhatsApp(data.id);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });
};

export const useDeleteAppointment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("appointments")
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["appointments"] });
      queryClient.invalidateQueries({ queryKey: ["enhanced-queue"] });
      queryClient.invalidateQueries({ queryKey: ["checked-in-appointments"] });
      queryClient.invalidateQueries({ queryKey: ["pending-communications"] });
      toast({
        title: "Success",
        description: "Appointment deleted successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });
};
